import axios from "axios"
import { useState } from "react"

import { L10n } from "@encoway/l10n"

import { selectCountry } from "../../app/configurationSlice"
import { useAppSelector } from "../../app/hooks"
import {
    hasVisualizationChanged,
    selectSavedConfigurationCode,
    selectVisualization
} from "../../app/visualizationSlice"
import { TranslationConstants } from "../../constants/translationConstants"
import { useDetermineResultBom } from "../../features/bom/useDetermineResultBom"
import { useSaveConfiguration } from "../../hooks/useSaveConfiguration"
import { FALLBACK_LANGUAGE } from "../../utils/constants"
import Dialog from "../Dialog/Dialog"
import CsvExportForm, { IFormData } from "./CsvExportForm"

interface CsvExportDialogProps {
    onClose: () => void
}

export default function CsvExportDialog(props: CsvExportDialogProps) {
    const [formSent, setFormSent] = useState(false)
    const [showError, setShowError] = useState(false)
    const visualization = useAppSelector(selectVisualization)
    const visualizationChanged = useAppSelector(hasVisualizationChanged)
    const { determineResultBom } = useDetermineResultBom()
    const baseUrl = window.encoway.baseUrl
    const savedConfigurationCode = useAppSelector(selectSavedConfigurationCode)
    const country = useAppSelector(selectCountry)
    const saveConfiguration = useSaveConfiguration()

    const onClose = () => {
        props.onClose()
        setFormSent(false)
        setShowError(false)
    }

    const onSubmit = async (formValues: IFormData) => {
        if (visualizationChanged) {
            await saveConfiguration((configurationCode) =>
                sendMail(
                    formValues,
                    country ?? FALLBACK_LANGUAGE,
                    configurationCode
                )
            )
        } else {
            await sendMail(formValues, country ?? FALLBACK_LANGUAGE)
        }
    }

    const sendMail = async (
        formValues: IFormData,
        salesContext: string,
        configurationCode?: string
    ) => {
        const formData = await createFormData(
            formValues,
            salesContext,
            configurationCode
        )
        await sendFormData(formData)
    }

    const createCsvFile = async () => {
        const baseUrl = window.encoway.baseUrl
        const csvData = await determineResultBom()
        if (csvData) {
            return await axios
                .post(`${baseUrl}/api/export/csvDownload`, { csvData }, {withCredentials: true})
                .then((response: any) => {
                    if (response.status === 200) {
                        return new Blob(["\uFEFF" + response.data], {
                            type: "text/csv;charset=utf-8"
                        })
                    }
                })
                .catch((error: any) =>
                    console.error(
                        "There was a problem with your fetch operation.",
                        error
                    )
                )
        }
        return null
    }

    const createFormData = async (
        formValues: IFormData,
        salesContext: string,
        configurationCode?: string
    ): Promise<FormData> => {
        const mailText = getMailText(
            formValues,
            salesContext,
            configurationCode
        )
        const csvFile = await createCsvFile()
        const renderedVisualization = await getVisualizationAsImage()
        const logo = await getLogo()

        const formData = new FormData()
        formData.append("mailAddress", formValues.mailAddress)
        formData.append(
            "mailSubject",
            L10n.format(TranslationConstants.Mail.MAIL_SUBJECT)
        )
        formData.append("mailText", mailText)
        formData.append(
            "mailFooter",
            L10n.format(TranslationConstants.Mail.MAIL_FOOTER)
        )

        csvFile && formData.append("csvFile", csvFile, "data.csv")

        if (renderedVisualization) {
            formData.append(
                "renderedVisualization",
                renderedVisualization,
                "renderedVisualization.zip"
            )
        }

        if (logo) {
            formData.append("logo", logo, "wago_logo.png")
        }

        return formData
    }

    const sendFormData = async (formData: FormData) => {
        await axios
            .post(`${baseUrl}/api/export/sendMail`, formData, {withCredentials: true})
            .then((response) => {
                if (response.status === 200) {
                    setFormSent(true)
                    setShowError(false)
                }
            })
            .catch((error) => {
                console.error("There was a problem sending the mail.", error)
                setShowError(true)
            })
    }

    const getMailText = (
        formValues: IFormData,
        salesContext: string,
        configurationCode?: string
    ) => {
        return L10n.format(TranslationConstants.Mail.MAIL_TEXT, {
            ...formValues,
            salesContext,
            comment: formValues.comment.replace(/\r\n|\r|\n/g, "<br/>"),
            configurationCode: configurationCode ?? savedConfigurationCode
        })
    }

    const getLogo = async (): Promise<Blob | null> => {
        try {
            const response = await axios.get("assets/wago_logo.png", {
                responseType: "blob",
                withCredentials: true
            })
            return response.data
        } catch (error) {
            console.error("Could not fetch logo.", error)
            return null
        }
    }

    const getVisualizationAsImage = async (): Promise<Blob | null> => {
        const floorPlanDimensions = getFloorPlanDimensions()
        try {
            const extension = await visualization.useExtension(
                "rendering",
                "1.11.3",
                visualization.cloud.token
            )
            // @ts-ignore
            return await extension.renderImages(visualization.cloud, {
                ...floorPlanDimensions,
                view: [],
                orthographic: true,
                near: 1000.0,
                far: 500000.0,
                fov: 1.0,
                dimensioning: {
                    type: "customDimensioning",
                    view: {
                        top: []
                    },
                    style: {
                        lineColor: "#c60f2d",
                        textColor: "#14303f"
                    }
                },
                lights: []
            })
        } catch (error) {
            console.error(
                "There was a problem rendering the image from visualization.",
                error
            )
            return null
        }
    }

    const getFloorPlanDimensions = () => {
        const floorPlan = visualization.cloud
            .graph()
            .nodes()
            // @ts-ignore
            .find((node) =>
                Object.keys(node.state.state).includes("floorplan_real_width")
            )
        if (floorPlan) {
            // FIXME: Why is the rendered image so small? Remove the multiplier!
            return {
                width: floorPlan.state.state.floorplan_real_width * 2,
                height: floorPlan.state.state.floorplan_real_height * 2
            }
        }
        return {}
    }

    return (
        <Dialog
            title={L10n.format(TranslationConstants.Dialogs.Mail.TITLE)}
            showPrimaryButton={true}
            showSecondaryButton={true}
            onClickSecondary={onClose}
            onClose={onClose}
            secondaryButtonTitle={
                formSent
                    ? L10n.format(
                          TranslationConstants.Dialogs.Mail.SECONDARY_BUTTON
                      )
                    : undefined
            }
        >
            {!formSent && <CsvExportForm onSubmit={onSubmit} />}
            {formSent && L10n.format(TranslationConstants.Mail.MAIL_SUCCESS)}
            {showError && L10n.format(TranslationConstants.Mail.MAIL_ERROR)}
        </Dialog>
    )
}
